import {
  EventEmitter,
  Injectable,
  NgZone,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import type { LngLatLike, PaddingOptions } from 'mapbox-gl';
import {
  CellGeoData,
  GeocoderCache,
} from '../../map/mapbox/services/geocoder-cache';
import { environment } from '../../../environments/environment';
import { ReportsService } from '../../reports/reports.service';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, Subject, Subscription } from 'rxjs';
import { JsonResponse } from '../api/backend-config';
import { finalize } from 'rxjs/operators';
import { geocoderContextToString } from '../util/geocoder-context-to-string';
import { FeatureCollection } from 'geojson';
import { UserAccessService } from '../../user/access/user-access.service';
import { MapBoxService, TypeOfMap } from '../../map/mapbox/mapbox.service';
import { RadiusSelectionService } from '../../reports/order-report/choose-location/choose-location-map/radius-selection.service';
import {
  LayerStoreService,
  LEVEL_LAYER,
} from '../../map/mapbox/services/layer-store.service';
import { BreakpointObserverService } from './breakpoint-observer.service';
import { UserInterestLocationService } from './user-interest-location.service';
import { GoogleAnalyticsService } from '../../user/google-analytics.service';
import { GeocodingApiService } from './geocoding-api.service';
import {
  isMapboxGeocoderLoaded,
  isMapboxGlLoaded,
} from '../mapbox/mapbox-loader';
import { PopupPlacementService } from '../../map/mapbox/services/popup-placement.service';
import { LevelSelectionService } from '../../map/mapbox/visualization/level-selection/level-selection.service';
import { geocoderResultLevel } from '../util/geocoder-result-level';

declare const mapboxgl: typeof import('mapbox-gl');
declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');

export type GeocoderSearchHistory = {
  id: string;
  address: string;
  lng: number;
  lat: number;
};

/**
 * How long `reverseGeocode` waits for a `result` event before giving up and
 * letting the caller fall back. Generous: this is a backstop for a geocoder
 * that will never answer, not a latency budget for one that is merely slow.
 */
const REVERSE_GEOCODE_TIMEOUT_MS = 8_000;

/** Room left between a searched state or county and the viewport edge. */
const AREA_BOUNDS_PADDING = 40;

@Injectable({
  providedIn: 'root',
})
export class SearchGeocoderService {
  public readonly geocoderCache = new GeocoderCache();

  private _reverseGeocoder?: MapboxGeocoder;

  /**
   * Built on first read rather than in field position. This service is
   * `providedIn: 'root'`, so a field initializer here would construct a
   * `MapboxGeocoder` - and therefore require the CDN bundles - the moment
   * anything injected the service. See `mapbox-loader.ts` for why that
   * mattered.
   *
   * Returns undefined rather than constructing when the plugin is missing.
   * `MapboxGeocoder` is a `declare const` global, so `new MapboxGeocoder()`
   * before the bundle executes throws a ReferenceError. A live map is not
   * evidence that it has: GL JS and the geocoder are two independent CDN
   * bundles fetched in parallel, and an ad blocker or a failed fetch leaves
   * this one absent for the life of the page while the map works fine.
   */
  public get reverseGeocoder(): MapboxGeocoder | undefined {
    if (!this._reverseGeocoder) {
      if (!isMapboxGeocoderLoaded()) return undefined;
      this._reverseGeocoder = new MapboxGeocoder({
        accessToken: environment.mapbox.accessToken,
        mapboxgl: (window as any).mapboxgl,
        countries: 'us',
        language: 'en',
        reverseGeocode: true,
      });
    }
    return this._reverseGeocoder;
  }

  public searchHistoryList: GeocoderSearchHistory[] = [];

  public isListVisible: boolean = false;

  readonly requestFocus$ = new Subject<void>();

  private _marker?: mapboxgl.Marker;

  /** Lazy for the same reason as `reverseGeocoder` above. */
  public get marker(): mapboxgl.Marker {
    if (!this._marker) {
      if (!isMapboxGlLoaded()) {
        console.error(
          '[mapbox] marker was read before GL JS finished loading - await loadMapboxGl() first'
        );
      }
      this._marker = new mapboxgl.Marker({ color: 'red' });
    }
    return this._marker;
  }
  // Save last search result coords to use instead of marker coords
  public lastSearchedCoordinates: [number, number] | null = null;

  private isSearchOpenedFirstTime = true;

  private inputEventListeners!: {
    handleClick: () => void;
    handleInput: () => void;
    handleBlur: () => void;
    handleControlButtons: (event: Event) => void;
  };

  private _clearListener?: () => void;
  private _resultListener?: (e: any) => void;
  // One geocoder is attached at a time (see _resultListener), so this holds
  // the focus subscription for the current one. Without dropping the previous
  // one, every re-open stacked another handler on this root service's Subject,
  // each keeping a detached geocoder alive.
  private _focusSub?: Subscription;

  // Suppress auto clear on geocoder detachment
  private suppressClear = false;

  private renderer: Renderer2;

  constructor(
    private reportsService: ReportsService,
    private userService: UserAccessService,
    rendererFactory: RendererFactory2,
    private http: HttpClient,
    private radiusSelectionService: RadiusSelectionService,
    private mapboxService: MapBoxService,
    private layerStore: LayerStoreService,
    private breakpointObserver: BreakpointObserverService,
    private userInterestLocationService: UserInterestLocationService,
    private gaService: GoogleAnalyticsService,
    private geocodingApi: GeocodingApiService,
    private popupPlacement: PopupPlacementService,
    private levelSelection: LevelSelectionService,
    private zone: NgZone
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public addGeocoderSearch(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder,
    searchClose: EventEmitter<void>,
    type?: TypeOfMap,
    popup?: mapboxgl.Popup
  ): void {
    if (!map.hasControl(searchGeocoder)) {
      this.suppressClear = true;

      const position = 'top-left';
      map.addControl(searchGeocoder, position);

      this._resultListener = (e) => {
        this.handleSearchResult(map, e, type);
        // un-focus geocoder input so mobile keyboard hides
        // @ts-ignore
        searchGeocoder._inputEl.blur();
      };
      searchGeocoder.on('result', this._resultListener);

      this._clearListener = () =>
        this.handleSearchClear(searchGeocoder, searchClose);
      searchGeocoder.on('clear', this._clearListener);

      this.handleInputEvents(searchGeocoder, popup);
    }
    // autofocus input after click
    // @ts-ignore
    searchGeocoder._inputEl.focus();
    this._focusSub?.unsubscribe();
    this._focusSub = this.requestFocus$.subscribe(() => {
      // @ts-ignore
      searchGeocoder._inputEl.focus();
    });

    // Somehow icon replacement doesnt work on first render so here we artificially remove first geocoder and add new one
    if (this.isSearchOpenedFirstTime) {
      this.isSearchOpenedFirstTime = false;

      this.removeGeocoderSearch(map, searchGeocoder);

      setTimeout(() => {
        this.addGeocoderSearch(map, searchGeocoder, searchClose, type, popup);
      }, 0);
    }

    // Add async wrapper to avoid races
    queueMicrotask(() => {
      this.suppressClear = false;
    });
  }

  public flyToAddress(
    point: LngLatLike,
    map: mapboxgl.Map,
    zoom?: number
  ): void {
    // Outside the zone: the map is built outside it, but a camera animation
    // started from in here inherits *this* zone for its whole flight, and
    // GL JS drives that flight with requestAnimationFrame - a patched one,
    // so every frame of it would run application-wide change detection.
    // Offset, not a bare centre: the popup that this flight ends in covers the
    // top-left of the map, so the cell has to land in the band beside it rather
    // than under it. See `getFocusOffset`.
    this.zone.runOutsideAngular(() =>
      map.flyTo({
        center: point,
        zoom: zoom,
        offset: this.popupPlacement.getFocusOffset(map),
      })
    );

    this.marker.setLngLat(point).addTo(map);

    this.mapboxService.map.once('idle', () => {
      this.mapboxService.simulateMapClick(point as [number, number]);
    });

    this.isListVisible = false;
  }

  public removeGeocoderSearch(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder
  ): void {
    if (this._resultListener) {
      searchGeocoder.off('result', this._resultListener);
    }

    if (this._clearListener) {
      searchGeocoder.off('clear', this._clearListener);
    }

    this._focusSub?.unsubscribe();
    this._focusSub = undefined;

    if (this.inputEventListeners) {
      const inputEl = (searchGeocoder as any)._inputEl;
      const { handleClick, handleInput, handleBlur, handleControlButtons } =
        this.inputEventListeners;

      inputEl.removeEventListener('click', handleClick);
      inputEl.removeEventListener('input', handleInput);
      inputEl.removeEventListener('blur', handleBlur);
      inputEl.removeEventListener('keydown', handleControlButtons);
    }

    map.removeControl(searchGeocoder);
  }

  private handleInputEvents(searchGeocoder: any, popup?: mapboxgl.Popup): void {
    const inputEl = searchGeocoder._inputEl;

    const handleClick = () => {
      // On input click active map popup should be closed
      if (popup && popup.isOpen()) {
        popup.remove();
      }

      if (searchGeocoder.inputString === '') {
        this.isListVisible = true;
        if (this.userService.getIsAuthorized()) {
          this.updateSearchHistoryList(searchGeocoder);
        }
      }
    };

    const handleInput = () => {
      this.isListVisible = false;
    };

    const handleBlur = () => {
      // Disable input clear on blur, probably will use it again
      // searchGeocoder.setInput('');

      setTimeout(() => {
        const focusedElement = document.activeElement as HTMLElement;
        if (
          !focusedElement.closest('.search-history-wrapper') &&
          !focusedElement.closest('.history-search-item')
        ) {
          this.isListVisible = false;
        }
      }, 200); // Delay is needed to still handle click on history item before closing list
    };

    // Artificially set input to blank space so that it wont close upon ctrl, cmd, shift press
    const handleControlButtons = (event: any) => {
      if (
        (event.ctrlKey || event.shiftKey || event.metaKey) &&
        !event.key.match(/^[a-zA-Z0-9]$/)
      ) {
        if (searchGeocoder.inputString === '') {
          searchGeocoder.setInput(' ');
        }

        return;
      }
    };

    inputEl.addEventListener('click', handleClick);
    inputEl.addEventListener('input', handleInput);
    inputEl.addEventListener('blur', handleBlur);
    inputEl.addEventListener('keydown', handleControlButtons);

    // Store references for later removal
    this.inputEventListeners = {
      handleClick,
      handleInput,
      handleBlur,
      handleControlButtons,
    };
  }

  public removeSearchItemFromHistory(
    id: string,
    geocoder: MapboxGeocoder
  ): void {
    this.http
      .delete(environment.apiUrl + `geocoding/my?searchId=${id}`)
      .pipe(
        finalize(() => {
          this.updateSearchHistoryList(geocoder);
        })
      )
      .subscribe(),
      catchError((err) => {
        console.error('ERROR removing from search history', err);
        return of(null);
      });
  }

  public saveSearchResult(
    result: CellGeoData
  ): Observable<JsonResponse<CellGeoData>> {
    return this.http.put<JsonResponse<CellGeoData>>(
      environment.apiUrl + 'geocoding',
      {
        address: result.place_name,
        lng: result.center[0],
        lat: result.center[1],
      }
    );
  }

  private updateSearchHistoryList(geocoder: any): void {
    this.http
      .get<
        JsonResponse<GeocoderSearchHistory[]>
      >(environment.apiUrl + 'geocoding/my')
      .subscribe((data) => {
        this.searchHistoryList = data.object;
        geocoder._inputEl.focus();
      }),
      catchError((err) => {
        console.error('ERROR fetching search history', err);
        return of(null);
      });
  }

  private handleSearchResult = (
    map: mapboxgl.Map,
    event: { result: CellGeoData },
    type?: TypeOfMap
  ): void => {
    this.marker.remove();
    const center = event.result.center;
    const ll = mapboxgl.LngLat.convert(center);

    this.saveSearchResult(event.result).subscribe(),
      catchError((err) => {
        console.error('ERROR saving search result', err);
        return of(null);
      });

    // A state or a county is a feature the map draws whole, so searching one
    // selects that feature rather than the 1-mile cell its label point happens
    // to fall in. Main map only: the level tool is what makes it meaningful,
    // and the report and comparison maps pick a point, not an area.
    const areaLevel =
      type === 'default' ? geocoderResultLevel(event.result) : null;

    // No pin on an area: `center` is only a label point, and dropping a marker
    // on it would claim a precision the result does not have.
    if (!areaLevel) {
      this.marker.setLngLat(center).addTo(map);
    }

    this.lastSearchedCoordinates = center;
    this.userInterestLocationService.setExplicit(ll.lat, ll.lng);

    if (areaLevel) {
      // Before the camera moves: the click that follows selects whatever the
      // *active* level layer draws under that pixel, so the state or county
      // polygon has to be the live one by the time the flight settles.
      this.levelSelection.selectLevel(areaLevel);
      this.flyToArea(map, event.result, areaLevel);
    } else {
      // Outside the zone: the map is built outside it, but a camera animation
      // started from in here inherits *this* zone for its whole flight, and
      // GL JS drives that flight with requestAnimationFrame - a patched one,
      // so every frame of it would run application-wide change detection.
      // See `flyToAddress`: on the main map this flight is followed by a popup
      // over the top-left corner, and `getFocusOffset` keeps the result clear
      // of it. It is a no-op for the report and comparison maps.
      this.zone.runOutsideAngular(() =>
        map.flyTo({
          center,
          zoom: 12,
          offset: this.popupPlacement.getFocusOffset(map),
        })
      );
    }

    if (type === 'default') {
      this.gaService.logSearchDone('suggestion');
    }

    if (type === 'default') {
      map.once('idle', () => {
        this.mapboxService.simulateMapClick(center);
      });
    }

    if (type === 'report') {
      // Instead of using simulateMapClick(), simulate feature creation without click
      this.mapboxService.removeAllMarkers(type);
      this.reportsService.deselectReportsFeature();

      // Same rule the map click handler follows: only the reports that
      // summarise an area get a circle. Searching an address used to draw one
      // unconditionally, so a `pin` report showed a trade area when you
      // searched and none when you clicked the same spot - with the panel
      // beside it still saying "This address".
      if (this.reportsService.selectedReportUsesRadius) {
        this.radiusSelectionService.renderCircle(ll);
      } else {
        this.radiusSelectionService.hideLayer();
      }

      this.reportsService.isLocationSelected = true;
      // Same record the map click keeps, so a location found by searching
      // survives stepping back to this page as well.
      this.reportsService.setSelectedPoint({ lng: ll.lng, lat: ll.lat });
      this.queryReverseGeocoder(ll);
    }
  };

  /**
   * Frames a searched state or county whole, inside the band of map the popup
   * leaves free, and never closer than the level tool tolerates - flying past
   * that ceiling would swap the polygon we are about to select for the cells
   * underneath it.
   *
   * `cameraForBounds` and then a plain `flyTo`, rather than `fitBounds`:
   * padding handed to a camera *move* is kept by the transform, so every later
   * `getCenter()` and URL sync would inherit a skew that is only true for this
   * one flight.
   */
  private flyToArea(
    map: mapboxgl.Map,
    result: CellGeoData,
    level: LEVEL_LAYER
  ): void {
    const maxZoom = this.layerStore.maxZoomKeepingLevel(
      level,
      this.breakpointObserver.isMobile.getValue()
    );

    // Undefined for a result mapbox knows no extent for, and for a viewport
    // too small to hold the padding below; both fall back to the label point.
    const camera = result.bbox
      ? map.cameraForBounds(result.bbox, {
          padding: this.areaBoundsPadding(map),
          maxZoom,
        })
      : undefined;

    this.zone.runOutsideAngular(() =>
      map.flyTo({
        center: camera?.center ?? result.center,
        zoom: camera?.zoom ?? maxZoom,
        // The padding already centred the area in the free band; only the
        // fallback still needs the camera shifted out from under the popup.
        offset: camera ? [0, 0] : this.popupPlacement.getFocusOffset(map),
      })
    );
  }

  /**
   * Bounds padding that keeps a fitted area clear of the popup. The popup
   * offset is half the width of the strip the card leaves unusable, so
   * reserving twice it on that side centres the area in what remains.
   */
  private areaBoundsPadding(map: mapboxgl.Map): PaddingOptions {
    const [dx] = this.popupPlacement.getFocusOffset(map);

    return {
      top: AREA_BOUNDS_PADDING,
      bottom: AREA_BOUNDS_PADDING,
      left: AREA_BOUNDS_PADDING + Math.max(dx, 0) * 2,
      right: AREA_BOUNDS_PADDING + Math.max(-dx, 0) * 2,
    };
  }

  private handleSearchClear = (
    searchGeocoder: MapboxGeocoder,
    searchClose: EventEmitter<void>
  ): void => {
    if (!this.suppressClear) {
      this.lastSearchedCoordinates = null;
    }

    // Clear event is being fired twice, so we needed to workaround with separate string variable that would store input
    this.marker.remove();
    this.isListVisible = false;

    // @ts-ignore
    const inputString = searchGeocoder.inputString.trim();

    if (!inputString || inputString.length === 0) {
      searchClose.emit();
    } else {
      // @ts-ignore
      searchGeocoder.inputString = '';
    }
  };

  public queryReverseGeocoder(coordinates: mapboxgl.LngLat): void {
    this.reverseGeocodeViaPlugin(coordinates.lat, coordinates.lng).then(
      (res) => {
        if (!res) return;
        // If we'll need more info on address like postal code, number of building etc. we can use res.place_name
        this.reportsService.setSelectedLocation(geocoderContextToString(res));
      }
    );
  }

  /**
   * Reverse-geocodes one point, resolving `null` instead of hanging when the
   * geocoder cannot answer. The single place that drives `reverseGeocoder`:
   * getting this wrong is easy and the failure is invisible, so callers get a
   * promise that always settles rather than the raw plugin.
   *
   * Three ways it can fail to emit `result`, all of which used to leave the
   * caller's promise pending forever:
   *  - the plugin never loaded, so there is no geocoder at all;
   *  - it loaded but nothing has called `addTo()` yet. `addTo` is what builds
   *    the plugin's DOM, and `query()` writes to that DOM, so on a detached
   *    geocoder it throws rather than issuing a request. Since the loader made
   *    `addTo` asynchronous this is reachable from a click on a freshly
   *    painted map;
   *  - the geocoding request itself is lost.
   *
   * Named `...ViaPlugin` to keep it distinct from `reverseGeocode` below,
   * which is a plain REST call and needs no Mapbox JS at all. That one is the
   * better mechanism; this exists because its callers consume the plugin's
   * `result` shape and cache, which is more than a rename to unpick.
   */
  public reverseGeocodeViaPlugin(
    lat: number,
    lng: number
  ): Promise<CellGeoData | null> {
    const geocoder = this.reverseGeocoder;
    if (!geocoder) {
      console.error(
        '[mapbox] reverse geocode skipped: the geocoder plugin is not loaded'
      );
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      let timer: ReturnType<typeof setTimeout>;

      const settle = (data: CellGeoData | null) => {
        clearTimeout(timer);
        geocoder.off('result', resultHandler);
        resolve(data);
      };

      const resultHandler = (event: any) => settle(event.result);

      timer = setTimeout(() => {
        console.error(
          `[mapbox] reverse geocode for ${lat},${lng} produced no result within ${REVERSE_GEOCODE_TIMEOUT_MS}ms`
        );
        settle(null);
      }, REVERSE_GEOCODE_TIMEOUT_MS);

      // Listener first: `query` is what starts the request, and registering
      // after it would be a race against a cached answer.
      geocoder.on('result', resultHandler);

      try {
        geocoder.query(`${lat},${lng}`);
      } catch (error) {
        console.error('[mapbox] reverse geocode query failed', error);
        settle(null);
      }
    });
  }

  // Both of these are plain REST calls that need no Mapbox JS at all, so they
  // live in GeocodingApiService and callers that want only them (the
  // sales-forecast address input) can inject that instead of this service.
  public forwardGeocode(input: string): Promise<any> {
    return this.geocodingApi.forwardGeocode(input);
  }

  // Takes a plain pair, not a `LngLat`: it only reads `.lng` and `.lat`, and
  // requiring the class would force callers that never touch a map to await
  // the Mapbox CDN bundle. A real `LngLat` still satisfies this.
  public reverseGeocode(coordinates: {
    lng: number;
    lat: number;
  }): Promise<FeatureCollection> {
    return this.geocodingApi.reverseGeocode(coordinates);
  }
}
